import React, { useRef } from "react";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ImageIcon from "@material-ui/icons/Image";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useTranslation } from "react-i18next";

import InfoPopover from "../../../../utils/informationButton";
import {
  TOUR_ICON_ALLOWED_TYPES,
  TOUR_ICON_RECOMMENDED_SIZE,
} from "../../../../constants/monetization";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: "1rem",
    fontWeight: 600,
    color: "#1a1f36",
  },
  helper: {
    fontSize: "0.75rem",
    color: "#697386",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  preview: {
    width: 72,
    height: 72,
    borderRadius: 12,
    objectFit: "cover",
    border: "1px solid #E0E0E0",
    backgroundColor: "white",
  },
  placeholder: {
    width: 72,
    height: 72,
    borderRadius: 12,
    border: "1px dashed #C4C9D4",
    backgroundColor: "#F8F9FA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#A0A6B4",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  uploadButton: {
    textTransform: "none",
    borderRadius: 8,
    fontWeight: 600,
    color: "#5E77FF",
    borderColor: "#5E77FF",
  },
  deleteButton: {
    textTransform: "none",
    borderRadius: 8,
    color: "#DC2626",
  },
  hiddenInput: {
    display: "none",
  },
}));

const TourEmailIconUpload = ({
  iconUrl,
  isUploading,
  isDeleting,
  onSelectFile,
  onDelete,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const handleChange = (event) => {
    const file = event.target.files && event.target.files[0];
    // Réinitialisé pour que resélectionner le même fichier redéclenche
    // l'événement (utile après un refus de validation).
    event.target.value = "";
    if (file) onSelectFile(file);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <ImageIcon style={{ fontSize: 22, color: "#3F51B5" }} />
        <Typography className={classes.title}>
          {t("tour_email_icon.title")}
        </Typography>
        <InfoPopover
          title={t("tour_email_icon.title")}
          text={t("tour_email_icon.info")}
        />
      </Box>
      <Typography className={classes.helper}>
        {t("tour_email_icon.helper", { size: TOUR_ICON_RECOMMENDED_SIZE })}
      </Typography>

      <Box className={classes.row}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={t("tour_email_icon.title")}
            className={classes.preview}
          />
        ) : (
          <Box className={classes.placeholder}>
            <ImageIcon style={{ fontSize: 28 }} />
          </Box>
        )}

        <Box className={classes.actions}>
          <Button
            variant="outlined"
            className={classes.uploadButton}
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={isUploading || isDeleting}
          >
            {isUploading ? (
              <CircularProgress size={20} />
            ) : iconUrl ? (
              t("tour_email_icon.replace")
            ) : (
              t("tour_email_icon.add")
            )}
          </Button>

          {iconUrl && (
            <Button
              className={classes.deleteButton}
              onClick={onDelete}
              disabled={isUploading || isDeleting}
              startIcon={
                isDeleting ? null : <DeleteOutlineIcon style={{ fontSize: 20 }} />
              }
            >
              {isDeleting ? (
                <CircularProgress size={20} />
              ) : (
                t("tour_email_icon.remove")
              )}
            </Button>
          )}
        </Box>
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={TOUR_ICON_ALLOWED_TYPES.join(",")}
        onChange={handleChange}
        className={classes.hiddenInput}
      />
    </Box>
  );
};

export default TourEmailIconUpload;
